// Tiny, dependency-free document-head manager (title / meta / OG / Twitter /
// canonical / JSON-LD). No @vueuse/head — the perf budget forbids the extra
// weight, and the storefront only needs a handful of tags per route.
//
// Model: App.vue calls setBaseHead() once bootstrap resolves (site name, default
// share image, locale). Each page calls useHead(getter) with its own overrides;
// every managed tag carries data-dk-head so we can upsert/remove cleanly on SPA
// navigation. When a page unmounts we fall back to the base head (so a route with
// no useHead() doesn't inherit the previous page's title/description).
//
// NOTE: injected client-side, so JS-rendering crawlers (Googlebot) see it, but
// non-JS social scrapers (Facebook/Zalo) will not until a bot-prerender layer
// lands (tracked in CLAUDE.md §9 "SEO"). Sitemap/robots + canonical + JSON-LD are
// server- or crawl-effective regardless.

import { onScopeDispose, watchEffect } from "vue"

export type JsonLd = Record<string, unknown>

export interface HeadInput {
  /** Page title; the site name is appended unless the title already is it. */
  title?: string
  description?: string
  /** Absolute canonical URL. Defaults to origin+path (query/hash stripped). */
  canonical?: string
  /** Share image (absolute, or root-relative — absolutised against origin). */
  image?: string
  /** og:type — "website" (default), "article", "product". */
  type?: string
  /** e.g. "noindex, follow" for private/transactional/search pages. */
  robots?: string
  /** JSON-LD structured data (one object or several). */
  jsonLd?: JsonLd | JsonLd[]
}

export interface BaseHead {
  siteName: string
  description?: string
  image?: string
  locale?: string
  /**
   * Store-wide `noindex`, for a shop that asks not to be in search results.
   *
   * It OVERRIDES the per-page value rather than defaulting it: a page that sets
   * `robots: "noindex, follow"` is narrowing its OWN visibility, and a store-wide
   * withholding has to win over that or the store is only partly withheld. Set by
   * the demo stores; no seller surface writes it today.
   */
  noindex?: boolean
}

const TITLE_SEP = " – "

let base: BaseHead = { siteName: "" }
let currentInput: HeadInput | null = null
let seq = 0
let activeOwner = 0

function absUrl(u: string | undefined): string | undefined {
  if (!u) return undefined
  if (/^https?:\/\//i.test(u)) return u
  if (u.startsWith("/")) return location.origin + u
  return u
}

function upsertMeta(attr: "name" | "property", key: string, content: string | undefined) {
  const sel = `meta[${attr}="${key}"][data-dk-head]`
  const existing = document.head.querySelector<HTMLMetaElement>(sel)
  if (!content) {
    existing?.remove()
    return
  }
  let el = existing
  if (!el) {
    el = document.createElement("meta")
    el.setAttribute(attr, key)
    el.setAttribute("data-dk-head", "")
    document.head.appendChild(el)
  }
  el.setAttribute("content", content)
}

function upsertLink(rel: string, href: string | undefined) {
  const sel = `link[rel="${rel}"][data-dk-head]`
  const existing = document.head.querySelector<HTMLLinkElement>(sel)
  if (!href) {
    existing?.remove()
    return
  }
  let el = existing
  if (!el) {
    el = document.createElement("link")
    el.setAttribute("rel", rel)
    el.setAttribute("data-dk-head", "")
    document.head.appendChild(el)
  }
  el.setAttribute("href", href)
}

function setJsonLd(data: JsonLd | JsonLd[] | undefined) {
  document.head.querySelectorAll("script[data-dk-head-ld]").forEach((n) => n.remove())
  if (!data) return
  const items = Array.isArray(data) ? data : [data]
  for (const item of items) {
    const s = document.createElement("script")
    s.type = "application/ld+json"
    s.setAttribute("data-dk-head-ld", "")
    // Escape "<" so a stray "</script>" or "<" in content can't break out.
    s.textContent = JSON.stringify(item).replace(/</g, "\\u003c")
    document.head.appendChild(s)
  }
}

function render() {
  const input = currentInput ?? {}
  const siteName = base.siteName || "Store"

  const title = input.title
  document.title = title && title !== siteName ? `${title}${TITLE_SEP}${siteName}` : siteName

  const description = input.description ?? base.description
  const canonical = input.canonical ?? location.origin + location.pathname
  const image = absUrl(input.image) ?? absUrl(base.image)
  const type = input.type ?? "website"

  upsertMeta("name", "description", description)
  upsertLink("canonical", canonical)
  upsertMeta("name", "robots", robotsValue(base.noindex, input.robots))

  // Open Graph.
  upsertMeta("property", "og:site_name", siteName)
  upsertMeta("property", "og:title", document.title)
  upsertMeta("property", "og:description", description)
  upsertMeta("property", "og:type", type)
  upsertMeta("property", "og:url", canonical)
  upsertMeta("property", "og:image", image)
  upsertMeta("property", "og:locale", base.locale)

  // Twitter card.
  upsertMeta("name", "twitter:card", image ? "summary_large_image" : "summary")
  upsertMeta("name", "twitter:title", document.title)
  upsertMeta("name", "twitter:description", description)
  upsertMeta("name", "twitter:image", image)

  setJsonLd(input.jsonLd)
}

/**
 * What the robots meta should say, given the store's setting and the page's.
 *
 * The STORE wins. A page that sets "noindex, follow" is narrowing its own
 * visibility — the cart, the search results — while a store-wide withholding is a
 * statement about the whole shop, so letting the page value through would leave
 * such a store only partly withheld, and the pages still in the index would be
 * precisely the ones worth indexing: products and collections.
 *
 * Undefined when neither asks for anything, so upsertMeta emits no tag at all
 * rather than an empty one a crawler would have to interpret.
 *
 * Pure and exported so the rule is testable in node — the rest of this module
 * writes to document.head, which the storefront's vitest has no DOM for.
 */
export function robotsValue(storeNoIndex: boolean | undefined, pageRobots: string | undefined): string | undefined {
  return storeNoIndex ? "noindex, nofollow" : pageRobots
}

/** Set site-wide defaults once bootstrap resolves. Re-renders the active page. */
export function setBaseHead(b: BaseHead) {
  base = b
  if (b.locale) document.documentElement.lang = b.locale
  render()
}

/** Per-page head. Reactive: re-renders when the getter's deps change; reverts to
 *  the base head when the owning component unmounts. */
export function useHead(getter: () => HeadInput) {
  const id = ++seq
  const stop = watchEffect(() => {
    const input = getter()
    activeOwner = id
    currentInput = input
    render()
  })
  onScopeDispose(() => {
    stop()
    if (activeOwner === id) {
      currentInput = null
      render()
    }
  })
}

/** Build an absolute URL for a store-relative path (canonical / JSON-LD urls). */
export function pageUrl(path?: string): string {
  return location.origin + (path ?? location.pathname)
}

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", "#39": "'" }

/**
 * Plain-text meta description from an HTML body, collapsed + clipped.
 *
 * Must stay in sync with stripHTMLText() in the API's prerender.go — the two
 * produce the description for the same page (this one for the SPA, that one for
 * non-JS scrapers), so a divergence shows up as two different previews.
 */
export function metaDescription(html: string | undefined, max = 160): string {
  if (!html) return ""
  const text = html
    // Drop these elements WITH their content: stripping tags alone left inline
    // CSS/JS as the description text (Shopify-imported pages open with <style>).
    .replace(/<(script|style|noscript)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, " ")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<[^>]*>/g, " ")
    // Decode rather than blank out, so "Tom &amp; Jerry" doesn't lose the "&".
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (_m, e: string) => {
      const k = e.toLowerCase()
      const named = ENTITIES[k]
      if (named !== undefined) return named
      if (k[0] === "#") {
        const n = k[1] === "x" ? parseInt(k.slice(2), 16) : parseInt(k.slice(1), 10)
        return Number.isFinite(n) && n > 0 && n <= 0x10ffff ? String.fromCodePoint(n) : " "
      }
      return " "
    })
    .replace(/\s+/g, " ")
    .trim()
  return text.length > max ? text.slice(0, max - 1).trimEnd() + "…" : text
}
